import { useEffect, useSyncExternalStore } from "react"

import type { DemoResetStatus } from "@/reset/contract"
import { RESET_STATUS_PATH } from "@/reset/manifest"

type ResetStatusSnapshot = {
  status: DemoResetStatus | null
  requestState: "idle" | "loading" | "ready" | "error"
}

const SERVER_SNAPSHOT: ResetStatusSnapshot = {
  status: null,
  requestState: "idle",
}

let snapshot: ResetStatusSnapshot = SERVER_SNAPSHOT
let pendingRequest: Promise<void> | null = null
const listeners = new Set<() => void>()

function emit(next: ResetStatusSnapshot) {
  snapshot = next
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function isStatus(value: unknown): value is DemoResetStatus {
  if (!value || typeof value !== "object") return false
  const candidate = value as Partial<DemoResetStatus>
  return (
    typeof candidate.scheduleTimeZone === "string" &&
    typeof candidate.scheduleUtcOffset === "string" &&
    typeof candidate.scheduleLocalTime === "string" &&
    typeof candidate.nextScheduledResetAt === "string" &&
    (candidate.lastSuccessfulResetAt === null ||
      typeof candidate.lastSuccessfulResetAt === "string") &&
    (candidate.currentRunStartedAt === null ||
      typeof candidate.currentRunStartedAt === "string") &&
    ["scheduled", "running", "failed", "overdue", "unavailable"].includes(
      candidate.state ?? ""
    ) &&
    typeof candidate.fixtureVersion === "string" &&
    Number.isInteger(candidate.resetGeneration)
  )
}

export function refreshDemoResetStatus() {
  if (typeof window === "undefined") return Promise.resolve()
  if (pendingRequest) return pendingRequest

  if (!snapshot.status) {
    emit({ ...snapshot, requestState: "loading" })
  }
  pendingRequest = fetch(RESET_STATUS_PATH, {
    headers: { accept: "application/json" },
    cache: "no-store",
  })
    .then(async (response) => {
      const value: unknown = await response.json()
      if (!isStatus(value)) {
        throw new Error("Reset status response is invalid.")
      }
      emit({ status: value, requestState: "ready" })
    })
    .catch(() => {
      emit({ status: snapshot.status, requestState: "error" })
    })
    .finally(() => {
      pendingRequest = null
    })

  return pendingRequest
}

function useResetStatusSnapshot() {
  return useSyncExternalStore(
    subscribe,
    () => snapshot,
    () => SERVER_SNAPSHOT
  )
}

export function useDemoResetStatus() {
  const current = useResetStatusSnapshot()

  useEffect(() => {
    void refreshDemoResetStatus()
    const onFocus = () => void refreshDemoResetStatus()
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        void refreshDemoResetStatus()
      }
    }
    const interval = window.setInterval(() => {
      void refreshDemoResetStatus()
    }, 30_000)

    window.addEventListener("focus", onFocus)
    document.addEventListener("visibilitychange", onVisibility)
    return () => {
      window.clearInterval(interval)
      window.removeEventListener("focus", onFocus)
      document.removeEventListener("visibilitychange", onVisibility)
    }
  }, [])

  return current
}

export function useDemoResetMaintenance() {
  const { status } = useResetStatusSnapshot()
  return status?.state === "running"
}
